import { hasPermission, type PermissionCheck } from '@sb/shared/permission/util';
import { localeSort, parseDate, toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { subDays, toDate } from 'date-fns';
import find from 'lodash-es/find';
import orderBy from 'lodash-es/orderBy';
import { createSelector } from 'reselect';

import { getPermissionState } from '../../../../reducers/auth/permission.helper';
import type { ContractModel } from '../../../../reducers/orm/contract/contract.model';
import { getContractEntities } from '../../../../reducers/orm/contract/contract.service';
import type { EmployeeModel } from '../../../../reducers/orm/employee/employee.model';
import {
  getActiveEmployees,
  getEmployees,
  getEmployeeTeamDepartmentsWithoutHiddenTeams,
} from '../../../../reducers/orm/employee/employee.selectors';

// `EmployeeModel.id` is optional because the same model doubles as the create payload, but an
// employee read back out of the store is always keyed by its id.
type StoredEmployee = EmployeeModel & { id: string };

type EmployeeWithContract = StoredEmployee & { contract: ContractModel | undefined };

/** An employee whose contract ends inside the requested period, so `enddate` is set. */
type EmployeeWithEndingContract = EmployeeWithContract & { enddate: string };

export const getEmployeeBirthdaysInPeriod = (minDate: string, maxDate: string) =>
  createSelector(
    getPermissionState,
    getEmployeeTeamDepartmentsWithoutHiddenTeams,
    getActiveEmployees,
    (permissionState, employeeTeamDepartments, activeEmployees) => {
      const filteredEmployees = activeEmployees.filter((employee) => {
        if (employee.birthday === null || employee.birthday < minDate || employee.birthday > maxDate) {
          return false;
        }

        const check: PermissionCheck = {
          userId: employee.id,
          departments: employeeTeamDepartments[employee.id],
          permissions: ['View all users'],
        };

        return hasPermission(check, permissionState);
      });

      return orderBy(filteredEmployees, ['birthday'], ['asc']);
    },
  );

export const getEmployeesWithContracts = createSelector(
  getEmployees,
  getContractEntities,
  (employees: StoredEmployee[], contracts): EmployeeWithContract[] =>
    employees.map((employee) => {
      const contract = find(contracts, (employeeContract) => employeeContract.user_id === employee.id);

      return {
        ...employee,
        contract,
      };
    }),
);

export const getEndingContractsForAuthenticatedUser = (minDate: string, maxDate: string) =>
  createSelector(
    getPermissionState,
    getEmployeeTeamDepartmentsWithoutHiddenTeams,
    getEmployeesWithContracts,
    (permissionState, employeeTeamDepartments, employees) => {
      const filteredEmployees = employees
        .filter((employee): employee is EmployeeWithEndingContract => {
          if (employee.enddate === null || employee.enddate <= minDate || employee.enddate > maxDate) {
            return false;
          }

          const check: PermissionCheck = {
            userId: employee.id,
            departments: employeeTeamDepartments[employee.id],
            permissions: ['View contracts'],
          };

          return hasPermission(check, permissionState);
        })
        .map((employee) => ({
          ...employee,
          enddate: toISODateString(subDays(parseDate(employee.enddate), 1)),
        }));

      return localeSort<(typeof filteredEmployees)[number]>(filteredEmployees, ['enddate', 'name'], ['asc', 'asc']);
    },
  );
